import type { PropsWithChildren } from "react";

import { useTranslate } from "@similarweb/i18n-provider";

import { NOT_AVAILABLE } from "app/filters/const";
import ManagementCard from "app/pages/sales-intelligence/modules/manage-integrations/components/cards-components/ManagementCard";
import ManagementCardFilterTag from "app/pages/sales-intelligence/modules/manage-integrations/components/cards-components/ManagementCardFilterTag";
import ManagementCardListItem from "app/pages/sales-intelligence/modules/manage-integrations/components/cards-components/ManagementCardListItem";
import ManagementCardTextBody from "app/pages/sales-intelligence/modules/manage-integrations/components/cards-components/ManagementCardTextBody";
import {
    StyledManagementCardButton,
    StyledManagementCardContentList,
    StyledManagementCardFooterWrapper,
    StyledManagementCardTagWrapper,
} from "app/pages/sales-intelligence/modules/manage-integrations/components/cards-components/styles";
import IntegrationButtonWrapper from "app/pages/sales-intelligence/modules/manage-integrations/components/integration-button/IntegrationButtonWrapper";
import {
    MANAGE_INTEGRATIONS_CARD_CONFIGURE,
    MANAGE_INTEGRATIONS_CARD_CONFIGURED,
    MANAGE_INTEGRATIONS_CARD_ENRICHMENT_TAG,
    MANAGE_INTEGRATIONS_CARD_MANAGE,
    MANAGE_INTEGRATIONS_CARD_UPGRADE,
    MY_INTEGRATIONS_API_SIGNALS_REST_API_CONFIGURED_TITLE,
    MY_INTEGRATIONS_API_SIGNALS_REST_API_DESCRIPTION,
    MY_INTEGRATIONS_API_SIGNALS_REST_API_TITLE,
} from "app/pages/sales-intelligence/modules/manage-integrations/constants/translations";
import type { ISignalsRestApiData } from "app/pages/sales-intelligence/modules/manage-integrations/hooks/useSignalsRestApiData";
import manageIntegrationsTrackingService from "app/pages/sales-intelligence/modules/manage-integrations/services/tracking";
import openExternalLink from "app/pages/sales-intelligence/modules/manage-integrations/utils/openExternalLink";
import {
    exploreSignalsRestApi,
    isSignalsRestApiAccountEnabled,
    manageSignalsRestApi,
} from "app/pages/sales-intelligence/modules/manage-integrations/utils/signalsIntegrationActions";
import { SIGNALS_REST_API_DOCS_URL } from "app/pages/sales-intelligence/modules/management/sub-modules/constants";
import { formatNumberWithComa } from "app/pages/sales-intelligence/utils/leads";

export const SignalsRestApiCard = ({
    isConfigured,
    signalsCount,
    children,
}: PropsWithChildren<ISignalsRestApiData>): React.ReactElement => {
    const { translate } = useTranslate();

    const isAccountEnabled = isSignalsRestApiAccountEnabled();

    const activeSignalsText = signalsCount ? formatNumberWithComa(signalsCount) : NOT_AVAILABLE;

    const handleGuideClick = () => {
        manageIntegrationsTrackingService.trackApiGuideClick("signalsRestApi");
        openExternalLink(SIGNALS_REST_API_DOCS_URL);
    };

    const handleManageClick = () => {
        manageSignalsRestApi(isConfigured);
    };

    const cardFooter = (
        <StyledManagementCardFooterWrapper>
            <StyledManagementCardTagWrapper>
                <ManagementCardFilterTag iconName="spend">{translate("10")}</ManagementCardFilterTag>
                <ManagementCardFilterTag iconName="spend">
                    {translate(MANAGE_INTEGRATIONS_CARD_ENRICHMENT_TAG)}
                </ManagementCardFilterTag>
                {children}
            </StyledManagementCardTagWrapper>

            <IntegrationButtonWrapper
                dataAutomationNameSpace="signals-rest-api-button"
                isEnabled={isAccountEnabled}
                upgradeButtonText={translate(MANAGE_INTEGRATIONS_CARD_UPGRADE)}
                onUpgradeClick={exploreSignalsRestApi}
            >
                <StyledManagementCardButton
                    buttonHtmlType="button"
                    type={isConfigured ? "outlined" : "primary"}
                    onClick={handleManageClick}
                    data-automation="signals-rest-api-button.integration-card-manage-button"
                >
                    {translate(isConfigured ? MANAGE_INTEGRATIONS_CARD_MANAGE : MANAGE_INTEGRATIONS_CARD_CONFIGURE)}
                </StyledManagementCardButton>
            </IntegrationButtonWrapper>
        </StyledManagementCardFooterWrapper>
    );

    const cardBody = isConfigured ? (
        <StyledManagementCardContentList>
            <ManagementCardListItem title={translate(MY_INTEGRATIONS_API_SIGNALS_REST_API_CONFIGURED_TITLE)}>
                {activeSignalsText}
            </ManagementCardListItem>
        </StyledManagementCardContentList>
    ) : (
        <ManagementCardTextBody>{translate(MY_INTEGRATIONS_API_SIGNALS_REST_API_DESCRIPTION)}</ManagementCardTextBody>
    );

    return (
        <ManagementCard
            dataAutomation="signals-rest-api-card"
            title={translate(MY_INTEGRATIONS_API_SIGNALS_REST_API_TITLE)}
            chipText={isConfigured ? translate(MANAGE_INTEGRATIONS_CARD_CONFIGURED) : undefined}
            chipType="success"
            onHeaderActionClick={!isConfigured ? handleGuideClick : undefined}
            footer={cardFooter}
        >
            {cardBody}
        </ManagementCard>
    );
};
